import React from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { ModalBody, ModalContainer, ModalHeader, ModalFooter } from '../../../components/ModalElements';
import Select from '../../../components/Select';
import { InlineRadioList, InlineRadioListItem, Label } from '../../../components/FormElements';
import { MediumButton, MediumPrimaryButton } from '../../../components/Buttons';
import { SessionTimelinePreferences } from '../timeline/SessionTimelineElements';
import { SectionLabel } from '../timeline/SessionTimelineContentElements';
import { Breakpoints, Color, Grid } from '../../../app/StyleGuide';
import useSessionTimeline from '../hooks/useSessionTimeline';
import styled from 'styled-components';
import { PrintPreferences } from './PrintableTimeline';
import { ToggleSwitch } from '../../../components/Switches';
import { FONT_SIZE_SCALE } from './PrintElements';

type FormValues = {
  fontSize: keyof typeof FONT_SIZE_SCALE;
  printStyle: 'color' | 'blackAndWhite';
  includeAgenda: boolean;
  media: boolean;
};

type PrintModalProps = {
  initialValues: FormValues;
  handleDismiss: () => void;
  onSubmit: (values: PrintPreferences) => void;
};

const PrintModalContainer = styled(ModalContainer)`
  @media print {
    display: none;
  }

  form {
    @media screen and (max-width: ${Breakpoints.screen_xs}) {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
    }

    @media screen and (max-height: ${Breakpoints.screen_xs}) and (orientation: landscape) {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
    }
  }
`;

const PrintModalBody = styled(ModalBody)`
  max-height: 500px;

  @media print {
    display: none;
  }

  @media screen and (max-width: ${Breakpoints.screen_xs}) {
    max-height: none;
    overflow-y: visible;
  }

  @media screen and (max-height: ${Breakpoints.screen_xs}) and (orientation: landscape) {
    max-height: none;
    overflow-y: visible;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: ${Grid._4};
`;

const LandscapeInlineRadioList = styled(InlineRadioList)`
  @media screen and (max-height: ${Breakpoints.screen_xs}) and (orientation: landscape) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }
`;

export default function PrintModal({ initialValues, handleDismiss, onSubmit }: PrintModalProps) {
  const { timeline, viewPreferenceMap, updateLeaderViewPreference } = useSessionTimeline();

  const handleSubmit = (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    onSubmit({ ...values, viewPreference: timeline.viewPreference || 'all' } as PrintPreferences);
    setSubmitting(false);
    handleDismiss();
  };

  const validationSchema = Yup.object({
    fontSize: Yup.number().required('Font size is required'),
    printStyle: Yup.string().required('Print style is required'),
  });

  return (
    <PrintModalContainer dismissHandler={handleDismiss}>
      <ModalHeader>
        <h3>Print Leader Guide</h3>
      </ModalHeader>
      <Formik<FormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ values, isSubmitting, setFieldValue }) => (
          <Form>
            <PrintModalBody>
              <FormContainer>
                <SessionTimelinePreferences>
                  <SectionLabel>Preferences</SectionLabel>
                  <LandscapeInlineRadioList>
                    {Object.entries(viewPreferenceMap).map(([key, value]) => (
                      <InlineRadioListItem
                        key={key}
                        title={value.title}
                        description={value.description}
                        checkedColor={Color.Blue._50}
                        checked={timeline.viewPreference === key}
                        onChange={() => {
                          updateLeaderViewPreference(key);
                        }}
                      />
                    ))}
                  </LandscapeInlineRadioList>
                </SessionTimelinePreferences>
                <div style={{ display: 'flex', flexDirection: 'row', gap: Grid._3 }}>
                  <div style={{ flex: 1 }}>
                    <Label>Font Size</Label>
                    <Select
                      options={[
                        {
                          label: 'Small',
                          value: 10,
                        },
                        {
                          label: 'Normal',
                          value: 12,
                        },
                        {
                          label: 'Medium',
                          value: 14,
                        },
                        {
                          label: 'Large',
                          value: 16,
                        },
                      ]}
                      value={values.fontSize}
                      onChange={value => setFieldValue('fontSize', value)}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Label>Print Style</Label>
                    <Select
                      options={[
                        { label: 'Color', value: 'color' },
                        { label: 'Black and White', value: 'blackAndWhite' },
                      ]}
                      value={values.printStyle}
                      onChange={value => setFieldValue('printStyle', value)}
                    />
                  </div>
                </div>

                <div>
                  <Label>Additional Options</Label>
                  <Label>
                    <span style={{ display: 'flex', alignItems: 'center', gap: Grid._3 }}>
                      <ToggleSwitch
                        id="includeAgenda"
                        name="includeAgenda"
                        label="Include Agenda"
                        checked={values.includeAgenda}
                        onChange={() => setFieldValue('includeAgenda', !values.includeAgenda)}
                        aria-label="Include Agenda Switch"
                      />
                    </span>
                  </Label>
                  <Label>
                    <span style={{ display: 'flex', alignItems: 'center', gap: Grid._3 }}>
                      <ToggleSwitch
                        id="media"
                        name="media"
                        label="Include Media"
                        checked={values.media}
                        onChange={() => setFieldValue('media', !values.media)}
                        aria-label="Include Media Switch"
                      />
                    </span>
                  </Label>
                </div>
              </FormContainer>
            </PrintModalBody>
            <ModalFooter>
              <MediumButton onClick={handleDismiss}>Cancel</MediumButton>
              <MediumPrimaryButton type="submit" disabled={isSubmitting} operating={isSubmitting}>
                Print Leader Guide
              </MediumPrimaryButton>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </PrintModalContainer>
  );
}
